import { html, raw } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { speakerChip, SPEAKERS } from './chrome.js';
import { concept } from '../lib/content.js';
import { isBookmarked, noteFor, settings } from '../lib/store.js';
import { speechSupported } from '../lib/speech.js';

// Who is speaking this verse (a quoted character wins over the narrator).
export function voiceOf(verse) {
  return verse.voice || verse.speaker;
}

function sanskritLines(verse) {
  const lines = verse.sa.map((line, i) => {
    const who = verse.lineSpeakers && verse.lineSpeakers[i];
    const prev = verse.lineSpeakers && i > 0 && verse.lineSpeakers[i - 1];
    const marker = who && prev && who !== prev
      ? html`<span class="verse__handover">${SPEAKERS[who].name} speaks</span>`
      : '';
    return html`${marker}<span class="verse__line">${line}</span>`;
  });
  return html`${lines}`;
}

export function termChips(ids = []) {
  const items = ids.map((id) => concept(id)).filter(Boolean);
  if (!items.length) return '';
  return html`<div class="verse__terms">
    <p class="eyebrow">Key ${items.length === 1 ? 'idea' : 'ideas'}</p>
    <div class="chips">${items.map((g) => html`<button type="button" class="chip term-chip" data-term="${g.id}"><span>${g.term}</span><span class="chip__deva" lang="sa">${g.sa}</span></button>`)}</div>
  </div>`;
}

export function verseTools(id, { listen = true } = {}) {
  const on = isBookmarked(id);
  const note = noteFor(id);
  return html`<div class="verse__tools">
    ${listen && speechSupported() ? html`<button type="button" class="icon-btn" data-listen="${id}" aria-label="Listen to verse ${id}" aria-pressed="false">${icon('listen', { size: 20 })}</button>` : ''}
    <button type="button" class="icon-btn" data-note="${id}" aria-label="${note ? 'Edit your note' : 'Add a note'} on verse ${id}" aria-pressed="${note ? 'true' : 'false'}">${icon('note', { size: 20 })}</button>
    <button type="button" class="icon-btn" data-bookmark="${id}" aria-label="Bookmark verse ${id}" aria-pressed="${on ? 'true' : 'false'}">${icon(on ? 'bookmark-on' : 'bookmark', { size: 20 })}</button>
  </div>`;
}

// The English is what the learner reads. The Sanskrit original sits in a fold
// under it: closed unless Settings > "Show the original Sanskrit" is on.
export function originalSanskrit(verse) {
  return html`<details class="verse__original"${settings().sanskrit ? raw(' open') : ''}>
    <summary><span>Original Sanskrit</span>${icon('chevron-down', { size: 18 })}</summary>
    <div class="verse__source">
      <p class="verse__sa deva" lang="sa">${sanskritLines(verse)}</p>
      <p class="verse__iast iast" lang="sa-Latn">${verse.iast.map((l) => html`<span class="verse__line">${l.replace(/\s*[।॥]+\s*$/, '')}</span>`)}</p>
    </div>
  </details>`;
}

// mode: 'lesson' (meaning open), 'reader' (meaning folded), 'page' (standalone)
export function verseBlock(ch, verse, { mode = 'lesson', headingLevel = 2 } = {}) {
  const id = `${ch}.${verse.v}`;
  const who = voiceOf(verse);
  const note = noteFor(id);
  const H = raw(`h${headingLevel}`);
  const meaning = html`
    <div class="verse__meaning prose">
      <p>${verse.meaning}</p>
    </div>
    ${verse.note ? html`<div class="callout verse__note">${icon('info', { size: 18 })}<p><strong>Readings differ.</strong> ${verse.note}</p></div>` : ''}
    ${termChips(verse.terms)}`;
  return html`<article class="verse verse--${mode}" id="v-${ch}-${verse.v}" data-verse="${id}" aria-labelledby="vh-${ch}-${verse.v}">
    <header class="verse__head">
      <${H} class="sr-only" id="vh-${ch}-${verse.v}">Verse ${id}, ${SPEAKERS[who] ? SPEAKERS[who].name : ''}</${H}>
      ${speakerChip(who, `${id}`)}
      ${verseTools(id)}
    </header>
    <p class="verse__tr">${verse.translation}</p>
    ${mode === 'reader'
      ? html`<details class="verse__more"><summary><span>What it means</span>${icon('chevron-down', { size: 18 })}</summary>${meaning}</details>`
      : html`<section class="verse__explain" aria-label="What it means"><p class="eyebrow verse__explain-label">What it means</p>${meaning}</section>`}
    ${originalSanskrit(verse)}
    ${note ? html`<button type="button" class="verse__mynote" data-note="${id}"><span class="eyebrow">Your note</span><span class="verse__mynote-text">${note.text}</span></button>` : ''}
  </article>`;
}

// Compact verse row for lists (search, bookmarks, concept pages).
export function verseRow(ch, verse, { sub = '' } = {}) {
  const id = `${ch}.${verse.v}`;
  return html`<a class="verse-row" href="/verse/${ch}/${verse.v}">
    <span class="verse-row__ref num">${id}</span>
    <span class="verse-row__body">
      <span class="verse-row__text">${verse.translation}</span>
      ${sub ? html`<span class="verse-row__sub">${sub}</span>` : ''}
    </span>
    ${icon('chevron-right', { size: 18, className: 'verse-row__chev' })}
  </a>`;
}
